import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import pointsService from '../services/pointsService';

const MotionCard = motion(Card);

export const CompactLevelCard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userProgress, setUserProgress] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Load user profile
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const profileData = userDoc.data();
          console.log('CompactLevelCard: Profile loaded:', !!profileData);
          setUserProfile(profileData);

          // Calculate and get user progress
          const progress = await pointsService.calculateXmLevel(currentUser.uid, profileData);
          console.log('CompactLevelCard: Progress calculated:', !!progress, progress);
          
          // If progress calculation fails, create a basic progress object
          if (!progress) {
            console.warn('CompactLevelCard: Progress calculation returned null, creating default progress');
            const defaultProgress = {
              currentXmLevel: 'none',
              xmPoints: 0,
              monthlyPoints: 0,
              profileCompleted: false,
              tradingExperienceCompleted: false,
              preferredPairsCompleted: false,
              xmAccountVerified: false,
              lastCalculated: new Date()
            };
            setUserProgress(defaultProgress);
          } else {
            setUserProgress(progress);
          }
        } else {
          console.log('CompactLevelCard: User document does not exist, creating default state');
          // Create default profile and progress for new users
          const defaultProfile = {};
          const defaultProgress = {
            currentXmLevel: 'none',
            xmPoints: 0,
            monthlyPoints: 0,
            profileCompleted: false,
            tradingExperienceCompleted: false,
            preferredPairsCompleted: false,
            xmAccountVerified: false,
            lastCalculated: new Date()
          };
          setUserProfile(defaultProfile);
          setUserProgress(defaultProgress);
        }
      } catch (error) {
        console.error('CompactLevelCard: Error loading user data:', error);
        // Create fallback progress even on error so card still shows
        const fallbackProgress = {
          currentXmLevel: 'none',
          xmPoints: 0,
          monthlyPoints: 0,
          profileCompleted: false,
          tradingExperienceCompleted: false,
          preferredPairsCompleted: false,
          xmAccountVerified: false,
          lastCalculated: new Date()
        };
        setUserProgress(fallbackProgress);
        setUserProfile({});
      } finally {
        setLoading(false);
      }
    };

    loadUserData();

    // Listen for level updates
    const handleLevelUpdate = () => {
      loadUserData();
    };

    window.addEventListener('userLevelUpdated', handleLevelUpdate);
    
    return () => {
      window.removeEventListener('userLevelUpdated', handleLevelUpdate);
    };
  }, [currentUser]);

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '⭐';
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return theme.palette.primary.main;
    }
  };

  const getNextLevelInfo = () => {
    const currentLevel = safeUserProgress?.currentXmLevel || 'none';
    const monthlyPoints = safeUserProgress?.monthlyPoints || 0; // Use monthly points for monthly system

    if (currentLevel === 'none') {
      return { nextLevel: 'bronze', target: 0, pointsNeeded: 0 }; // Bronze is profile-based, not points
    } else if (currentLevel === 'bronze') {
      return { nextLevel: 'silver', target: 500, pointsNeeded: Math.max(0, 500 - monthlyPoints) };
    } else if (currentLevel === 'silver') {
      return { nextLevel: 'gold', target: 1000, pointsNeeded: Math.max(0, 1000 - monthlyPoints) };
    } else {
      return { nextLevel: 'max', target: 1000, pointsNeeded: 0 };
    }
  };

  const getCompletionPercentage = () => {
    const currentLevel = safeUserProgress?.currentXmLevel || 'none';
    const monthlyPoints = safeUserProgress?.monthlyPoints || 0; // Use monthly points
    const nextLevelInfo = getNextLevelInfo();

    if (currentLevel === 'none') {
      // For Bronze, we need to check profile completion
      const bronzeProgress = pointsService.getBronzeProgress(userProfile || {});
      return bronzeProgress.completionPercentage;
    } else if (currentLevel === 'bronze') {
      return Math.min(100, Math.round((monthlyPoints / 500) * 100));
    } else if (currentLevel === 'silver') {
      return Math.min(100, Math.round((monthlyPoints / 1000) * 100));
    } else {
      return 100; // Gold is max level
    }
  };

  // Don't render if no current user
  if (!currentUser) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          width: '100%',
          maxWidth: '400px',
          mx: 'auto'
        }}
      >
        <Card sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading progress...
          </Typography>
        </Card>
      </Box>
    );
  }

  // At this point, we should always have userProgress due to our fallback logic above
  // But just in case, create a minimal progress object
  const safeUserProgress = userProgress || {
    currentXmLevel: 'none',
    xmPoints: 0,
    monthlyPoints: 0
  };

  const currentLevel = safeUserProgress.currentXmLevel || 'none';
  const monthlyPoints = safeUserProgress.monthlyPoints || 0; // Use monthly points for display
  const nextLevelInfo = getNextLevelInfo();
  const completionPercentage = getCompletionPercentage();
  const isMaxLevel = currentLevel === 'gold';

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: '400px',
        mx: 'auto'
      }}
    >
      <MotionCard
        initial={{ opacity: 0, x: isMobile ? 0 : 50, y: isMobile ? 20 : 0 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.3 }}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${getLevelColor(currentLevel)}10 100%)`,
          border: `1px solid ${getLevelColor(currentLevel)}40`,
          boxShadow: theme.shadows[4],
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 2 }, '&:last-child': { pb: { xs: 2.5, md: 2 } } }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant={isExtraSmall ? "h5" : "h4"}>{getLevelIcon(currentLevel)}</Typography>
              <Box>
                <Typography 
                  variant={isExtraSmall ? "caption" : "body2"} 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}
                >
                  {currentLevel === 'none' ? 'Getting Started' : `${currentLevel.charAt(0).toUpperCase() + currentLevel.slice(1)} Level`}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', md: '0.75rem' } }}
                >
                  {safeUserProgress.xmPoints || 0} XM Points
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${completionPercentage}%`}
              size="small"
              sx={{
                backgroundColor: `${getLevelColor(currentLevel)}20`,
                color: getLevelColor(currentLevel),
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                height: { xs: 24, md: 24 },
              }}
            />
          </Box>

          {/* Progress Section */}
          {!isMaxLevel && (
            <>
              <Box mb={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Progress to {nextLevelInfo.nextLevel.charAt(0).toUpperCase() + nextLevelInfo.nextLevel.slice(1)}
                  </Typography>
                  {currentLevel !== 'none' && (
                    <Typography 
                      variant="caption" 
                      fontWeight="bold" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                    >
                      {monthlyPoints}/{nextLevelInfo.target} pts
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={completionPercentage}
                  sx={{
                    height: { xs: 6, md: 4 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.action.hover,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${getLevelColor(currentLevel)}, ${getLevelColor(nextLevelInfo.nextLevel)})`
                    },
                  }}
                />
              </Box>

              {/* Bronze Requirements */}
              {currentLevel === 'none' && (
                <Box sx={{ mt: 1 }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    fontWeight="bold" 
                    sx={{ 
                      display: 'block', 
                      mb: 0.5,
                      fontSize: { xs: '0.7rem', md: '0.75rem' }
                    }}
                  >
                    Complete Bronze Requirements:
                  </Typography>
                  {(() => {
                    const bronzeProgress = pointsService.getBronzeProgress(userProfile || {});
                    const incomplete = Object.entries(bronzeProgress.requirements)
                      .filter(([_, req]: [string, any]) => !req.completed)
                      .slice(0, isExtraSmall ? 2 : 3); // Show all 3 requirements on regular screens, 2 on very small screens
                    
                    const needsProfileLink = incomplete.some(([key]) => key === 'tradingExperience' || key === 'preferredPairs');

                    if (incomplete.length === 0) {
                      return (
                        <Typography 
                          variant="caption" 
                          color="success.main"
                          sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                        >
                          ✅ Ready for Bronze level!
                        </Typography>
                      );
                    }
                    
                    return (
                      <>
                        {incomplete.map(([key, req]: [string, any]) => (
                          <Typography 
                            key={key} 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'block', 
                              fontSize: { xs: '0.65rem', md: '0.7rem' },
                              lineHeight: 1.3
                            }}
                          >
                            • {req.label}
                          </Typography>
                        ))}

                        {needsProfileLink && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1, fontSize: '0.7rem' }}
                            onClick={() => navigate('/profile')}
                          >
                            Complete in Profile
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Points Needed for Silver/Gold */}
              {currentLevel !== 'none' && nextLevelInfo.pointsNeeded > 0 && (
                <Box textAlign="center" sx={{ mt: 1 }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    <strong>{nextLevelInfo.pointsNeeded} more points</strong> needed for {nextLevelInfo.nextLevel}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Max Level Message */}
          {isMaxLevel && (
            <Box textAlign="center" sx={{ mt: 1 }}>
              <Typography 
                variant="caption" 
                color={getLevelColor(currentLevel)} 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
              >
                🏆 Maximum Level Achieved!
              </Typography>
            </Box>
          )}
        </CardContent>
      </MotionCard>
    </Box>
  );
}; 